import React, { useState, useEffect } from 'react';
import { Layout, FileText, BarChart3, FileCheck, Radio, ClipboardList, Sparkles } from 'lucide-react';

const StockShelvesIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <span
    className="material-symbols-outlined"
    style={{ fontSize: size, lineHeight: 1, display: 'inline-flex' }}
  >
    package_2
  </span>
);

type TabType = 'designer' | 'sites' | 'jobs' | 'reports' | 'proposal' | 'analytics' | 'stock' | 'whatsnew' | 'tower3d';

interface NavigationSidebarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  activeTab,
  onTabChange,
}) => {
  const [hoveredTab, setHoveredTab] = useState<TabType | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [topOffset, setTopOffset] = useState(48);

  useEffect(() => {
    const updateTopOffset = () => {
      const header = document.querySelector('header');
      if (header) {
        setTopOffset(header.getBoundingClientRect().height);
      } else {
        setTopOffset(48);
      }
    };

    updateTopOffset();
    window.addEventListener('resize', updateTopOffset);
    return () => {
      window.removeEventListener('resize', updateTopOffset);
    };
  }, []);

  const tabs = [
    { id: 'designer' as TabType, label: 'Designer', icon: Layout },
    { id: 'sites' as TabType, label: 'Sites', icon: Radio },
    { id: 'jobs' as TabType, label: 'Jobs', icon: ClipboardList },
    { id: 'reports' as TabType, label: 'Reports', icon: FileText },
    { id: 'proposal' as TabType, label: 'Proposal', icon: FileCheck },
    { id: 'analytics' as TabType, label: 'Analytics', icon: BarChart3 },
    { id: 'stock' as TabType, label: 'Stock Control', icon: StockShelvesIcon },
    { id: 'whatsnew' as TabType, label: "What's New", icon: Sparkles },
  ];

  const handleMouseEnter = (e: React.MouseEvent, tabId: TabType) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredTab(tabId);
    setTooltipPosition({
      x: rect.right + 8,
      y: rect.top + rect.height / 2,
    });
  };

  const handleMouseLeave = () => {
    setHoveredTab(null);
    setTooltipPosition(null);
  };

  return (
    <>
      <div
        className="w-12 bg-slate-900 border-r border-slate-700 flex flex-col items-center pb-3 fixed left-0 z-[60]"
        style={{ top: `${topOffset}px`, height: `calc(100vh - ${topOffset}px)`, paddingTop: 0, marginTop: 0 }}
      >
        <div className="flex flex-col items-center flex-1" style={{ gap: '6px', marginTop: '0px', paddingTop: 0 }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <div
                key={tab.id}
                className="relative group"
                onMouseEnter={(e) => handleMouseEnter(e, tab.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => onTabChange(tab.id)}
                  aria-label={tab.label}
                  className={`
                    w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200
                    ${isActive
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }
                  `}
                >
                  <Icon size={18} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {hoveredTab && tooltipPosition && (
        <div
          className="fixed z-[100] pointer-events-none"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateY(-50%)',
          }}
        >
          <div className="bg-slate-800 text-white text-sm px-3 py-2 rounded-lg shadow-xl border border-slate-700 whitespace-nowrap">
            {tabs.find((t) => t.id === hoveredTab)?.label || ''}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-slate-800"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavigationSidebar;
